import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Clock, 
  FileText, 
  Trash2, 
  RefreshCw, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  User,
  Building,
  Calendar,
  Search,
  Filter,
  ChevronDown,
  Eye,
  RotateCcw,
  Activity,
  Globe,
  Monitor,
  MapPin,
  AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SecurityAuditTab = ({ hospitals = [], isHospitalAdmin = false, hospitalId = null }) => {
  const [activeSubTab, setActiveSubTab] = useState('login-history');
  const [loading, setLoading] = useState(false);
  
  // Login History State
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginFilters, setLoginFilters] = useState({
    hospital_id: hospitalId || '',
    role: '',
    status: '',
    start_date: '',
    end_date: ''
  });
  const [loginTotal, setLoginTotal] = useState(0);
  
  // Audit Logs State
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilters, setAuditFilters] = useState({
    hospital_id: hospitalId || '',
    record_type: '',
    action: '',
    start_date: '',
    end_date: ''
  });
  const [auditTotal, setAuditTotal] = useState(0);
  
  // Deleted Records State
  const [deletedRecords, setDeletedRecords] = useState([]);
  const [deletedFilters, setDeletedFilters] = useState({
    hospital_id: hospitalId || '',
    record_type: '',
    include_restored: false,
    start_date: '',
    end_date: ''
  });
  const [deletedTotal, setDeletedTotal] = useState(0);
  
  // Failed Login Attempts State
  const [failedLogins, setFailedLogins] = useState(null);
  const [failedLoginHours, setFailedLoginHours] = useState(24);
  
  // Summary Stats
  const [auditSummary, setAuditSummary] = useState(null);
  
  // Dialog States
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailType, setDetailType] = useState('');
  
  // Permanent Delete States
  const [showPermanentDeleteDialog, setShowPermanentDeleteDialog] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [permanentDeleteConfirmText, setPermanentDeleteConfirmText] = useState('');
  const [deletingPermanently, setDeletingPermanently] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    loadData();
  }, [activeSubTab, page]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeSubTab) {
        case 'login-history':
          await loadLoginHistory();
          break;
        case 'audit-logs':
          await loadAuditLogs();
          break;
        case 'deleted-records':
          await loadDeletedRecords();
          break;
        case 'failed-logins':
          await loadFailedLogins();
          break;
        case 'summary':
          await loadAuditSummary();
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadLoginHistory = async () => {
    const params = new URLSearchParams();
    if (loginFilters.hospital_id) params.append('hospital_id', loginFilters.hospital_id);
    if (loginFilters.role) params.append('role', loginFilters.role);
    if (loginFilters.status) params.append('status', loginFilters.status);
    if (loginFilters.start_date) params.append('start_date', loginFilters.start_date);
    if (loginFilters.end_date) params.append('end_date', loginFilters.end_date);
    params.append('limit', pageSize);
    params.append('skip', page * pageSize);
    
    const response = await axios.get(`${API}/security/login-history?${params.toString()}`);
    setLoginHistory(response.data.records);
    setLoginTotal(response.data.total);
  };

  const loadAuditLogs = async () => {
    const params = new URLSearchParams();
    if (auditFilters.hospital_id) params.append('hospital_id', auditFilters.hospital_id);
    if (auditFilters.record_type) params.append('record_type', auditFilters.record_type);
    if (auditFilters.action) params.append('action', auditFilters.action);
    if (auditFilters.start_date) params.append('start_date', auditFilters.start_date);
    if (auditFilters.end_date) params.append('end_date', auditFilters.end_date);
    params.append('limit', pageSize);
    params.append('skip', page * pageSize);
    
    const response = await axios.get(`${API}/security/audit-logs?${params.toString()}`);
    setAuditLogs(response.data.records);
    setAuditTotal(response.data.total);
  };

  const loadDeletedRecords = async () => {
    const params = new URLSearchParams();
    if (deletedFilters.hospital_id) params.append('hospital_id', deletedFilters.hospital_id);
    if (deletedFilters.record_type) params.append('record_type', deletedFilters.record_type);
    if (deletedFilters.include_restored) params.append('include_restored', 'true');
    if (deletedFilters.start_date) params.append('start_date', deletedFilters.start_date);
    if (deletedFilters.end_date) params.append('end_date', deletedFilters.end_date);
    params.append('limit', pageSize);
    params.append('skip', page * pageSize);
    
    const response = await axios.get(`${API}/security/deleted-records?${params.toString()}`);
    setDeletedRecords(response.data.records);
    setDeletedTotal(response.data.total);
  };

  const loadFailedLogins = async () => {
    const params = new URLSearchParams();
    params.append('hours', failedLoginHours);
    if (hospitalId) params.append('hospital_id', hospitalId);
    
    const response = await axios.get(`${API}/security/failed-logins?${params.toString()}`);
    setFailedLogins(response.data);
  };

  const loadAuditSummary = async () => {
    const params = new URLSearchParams();
    params.append('days', 30);
    if (hospitalId) params.append('hospital_id', hospitalId);
    
    const response = await axios.get(`${API}/security/audit-summary?${params.toString()}`);
    setAuditSummary(response.data);
  };

  const handleRestoreRecord = async (deletedId) => {
    const admin = JSON.parse(localStorage.getItem('centralAdmin') || '{}');
    
    try {
      await axios.post(`${API}/security/deleted-records/${deletedId}/restore`, null, {
        params: {
          restored_by_id: admin.username || 'admin',
          restored_by_name: admin.name || 'Central Admin'
        }
      });
      toast.success('Record restored successfully');
      loadDeletedRecords();
    } catch (error) {
      console.error('Error restoring record:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore record');
    }
  };

  const openPermanentDeleteDialog = (record) => {
    setRecordToDelete(record);
    setPermanentDeleteConfirmText('');
    setShowPermanentDeleteDialog(true);
  };

  const handlePermanentDelete = async () => {
    if (permanentDeleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    const admin = JSON.parse(localStorage.getItem('centralAdmin') || '{}');
    setDeletingPermanently(true);
    
    try {
      await axios.delete(`${API}/security/deleted-records/${recordToDelete.id}/permanent`, {
        params: {
          deleted_by_id: admin.username || 'admin',
          deleted_by_name: admin.name || 'Central Admin'
        }
      });
      toast.success('Record permanently deleted');
      setShowPermanentDeleteDialog(false);
      setRecordToDelete(null);
      setPermanentDeleteConfirmText('');
      loadDeletedRecords();
    } catch (error) {
      console.error('Error permanently deleting record:', error);
      toast.error(error.response?.data?.detail || 'Failed to permanently delete record');
    } finally {
      setDeletingPermanently(false);
    }
  };

  const handleApplyFilters = () => {
    setPage(0);
    loadData();
  };

  const handleResetFilters = () => {
    setPage(0);
    switch (activeSubTab) {
      case 'login-history':
        setLoginFilters({
          hospital_id: hospitalId || '',
          role: '',
          status: '',
          start_date: '',
          end_date: ''
        });
        break;
      case 'audit-logs':
        setAuditFilters({
          hospital_id: hospitalId || '',
          record_type: '',
          action: '',
          start_date: '',
          end_date: ''
        });
        break;
      case 'deleted-records':
        setDeletedFilters({
          hospital_id: hospitalId || '',
          record_type: '',
          include_restored: false,
          start_date: '',
          end_date: ''
        });
        break;
    }
    setTimeout(loadData, 100);
  };

  const viewRecordDetail = (record, type) => {
    setSelectedRecord(record);
    setDetailType(type);
    setShowDetailDialog(true);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      doctor: { color: 'bg-purple-100 text-purple-800', label: 'Doctor' },
      frontdesk: { color: 'bg-green-100 text-green-800', label: 'Front Desk' },
      hospital_admin: { color: 'bg-blue-100 text-blue-800', label: 'Hospital Admin' },
      central_admin: { color: 'bg-red-100 text-red-800', label: 'Central Admin' }
    };
    const config = roleConfig[role] || { color: 'bg-gray-100 text-gray-800', label: role };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  const getStatusBadge = (status) => {
    if (status === 'success') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3" /> Success
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <XCircle className="w-3 h-3" /> Failed
      </span>
    );
  };

  const getActionBadge = (action) => {
    const actionConfig = {
      create: { color: 'bg-green-100 text-green-800' },
      update: { color: 'bg-blue-100 text-blue-800' },
      delete: { color: 'bg-red-100 text-red-800' },
      restore: { color: 'bg-purple-100 text-purple-800' }
    };
    const config = actionConfig[action] || { color: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${config.color}`}>{action}</span>;
  };

  const getHospitalName = (hospitalId) => {
    const hospital = hospitals.find(h => h.id === hospitalId);
    return hospital?.name || hospitalId || 'N/A';
  };

  const subTabs = [
    { id: 'login-history', label: 'Login History', icon: Clock },
    { id: 'audit-logs', label: 'Data Changes', icon: FileText },
    { id: 'deleted-records', label: 'Deleted Records', icon: Trash2 },
    { id: 'failed-logins', label: 'Failed Logins', icon: AlertTriangle },
    { id: 'summary', label: 'Summary', icon: Activity }
  ];

  return (
    <div className="space-y-6" data-testid="security-audit-tab">
      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-4">
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveSubTab(tab.id); setPage(0); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeSubTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            data-testid={`security-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Login History Tab */}
      {activeSubTab === 'login-history' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2 text-gray-700 font-medium">
              <Filter className="w-4 h-4" />
              Filters
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {!isHospitalAdmin && (
                <div>
                  <Label className="text-xs">Hospital</Label>
                  <Select 
                    value={loginFilters.hospital_id || 'all'} 
                    onValueChange={(v) => setLoginFilters({...loginFilters, hospital_id: v === 'all' ? '' : v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="All Hospitals" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Hospitals</SelectItem>
                      {hospitals.map(h => (
                        <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs">Role</Label>
                <Select 
                  value={loginFilters.role || 'all'} 
                  onValueChange={(v) => setLoginFilters({...loginFilters, role: v === 'all' ? '' : v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="doctor">Doctor</SelectItem>
                    <SelectItem value="frontdesk">Front Desk</SelectItem>
                    <SelectItem value="hospital_admin">Hospital Admin</SelectItem>
                    <SelectItem value="central_admin">Central Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select 
                  value={loginFilters.status || 'all'} 
                  onValueChange={(v) => setLoginFilters({...loginFilters, status: v === 'all' ? '' : v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">From Date</Label>
                <Input 
                  type="date" 
                  value={loginFilters.start_date}
                  onChange={(e) => setLoginFilters({...loginFilters, start_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">To Date</Label>
                <Input 
                  type="date" 
                  value={loginFilters.end_date}
                  onChange={(e) => setLoginFilters({...loginFilters, end_date: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleApplyFilters} size="sm">
                <Search className="w-4 h-4 mr-1" /> Apply
              </Button>
              <Button variant="outline" onClick={handleResetFilters} size="sm">
                Reset
              </Button>
            </div>
          </div>

          {/* Results */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {loginTotal} login records found
              </span>
              <Button variant="ghost" size="sm" onClick={loadData} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium">User</th>
                    <th className="text-left p-3 font-medium">Role</th>
                    {!isHospitalAdmin && <th className="text-left p-3 font-medium">Hospital</th>}
                    <th className="text-left p-3 font-medium">Login Time</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">IP Address</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {loginHistory.length === 0 ? (
                    <tr>
                      <td colSpan={isHospitalAdmin ? 6 : 7} className="p-8 text-center text-gray-500">
                        No login records found
                      </td>
                    </tr>
                  ) : (
                    loginHistory.map(record => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="p-3">
                          <div className="font-medium">{record.user_name}</div>
                          <div className="text-xs text-gray-500">{record.user_id}</div>
                        </td>
                        <td className="p-3">{getRoleBadge(record.role)}</td>
                        {!isHospitalAdmin && (
                          <td className="p-3 text-gray-600">{getHospitalName(record.hospital_id)}</td>
                        )}
                        <td className="p-3">{formatDateTime(record.login_time)}</td>
                        <td className="p-3">{getStatusBadge(record.status)}</td>
                        <td className="p-3 font-mono text-xs">{record.ip_address || 'N/A'}</td>
                        <td className="p-3">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => viewRecordDetail(record, 'login')}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {loginTotal > pageSize && (
              <div className="p-4 border-t flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Page {page + 1} of {Math.ceil(loginTotal / pageSize)}
                </span>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={(page + 1) * pageSize >= loginTotal}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit Logs Tab */}
      {activeSubTab === 'audit-logs' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2 text-gray-700 font-medium">
              <Filter className="w-4 h-4" />
              Filters
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {!isHospitalAdmin && (
                <div>
                  <Label className="text-xs">Hospital</Label>
                  <Select 
                    value={auditFilters.hospital_id || 'all'} 
                    onValueChange={(v) => setAuditFilters({...auditFilters, hospital_id: v === 'all' ? '' : v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="All Hospitals" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Hospitals</SelectItem>
                      {hospitals.map(h => (
                        <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs">Record Type</Label>
                <Select 
                  value={auditFilters.record_type || 'all'} 
                  onValueChange={(v) => setAuditFilters({...auditFilters, record_type: v === 'all' ? '' : v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="patient">Patient</SelectItem>
                    <SelectItem value="prescription">Prescription</SelectItem>
                    <SelectItem value="appointment">Appointment</SelectItem>
                    <SelectItem value="doctor">Doctor</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="hospital">Hospital</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Action</Label>
                <Select 
                  value={auditFilters.action || 'all'} 
                  onValueChange={(v) => setAuditFilters({...auditFilters, action: v === 'all' ? '' : v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    <SelectItem value="create">Create</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                    <SelectItem value="delete">Delete</SelectItem>
                    <SelectItem value="restore">Restore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">From Date</Label>
                <Input 
                  type="date" 
                  value={auditFilters.start_date}
                  onChange={(e) => setAuditFilters({...auditFilters, start_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">To Date</Label>
                <Input 
                  type="date" 
                  value={auditFilters.end_date}
                  onChange={(e) => setAuditFilters({...auditFilters, end_date: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleApplyFilters} size="sm">
                <Search className="w-4 h-4 mr-1" /> Apply
              </Button>
              <Button variant="outline" onClick={handleResetFilters} size="sm">
                Reset
              </Button>
            </div>
          </div>

          {/* Results */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {auditTotal} audit records found
              </span>
              <Button variant="ghost" size="sm" onClick={loadData} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium">Timestamp</th>
                    <th className="text-left p-3 font-medium">Record Type</th>
                    <th className="text-left p-3 font-medium">Action</th>
                    <th className="text-left p-3 font-medium">Changed By</th>
                    <th className="text-left p-3 font-medium">Field</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {auditLogs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-gray-500">
                        No audit logs found
                      </td>
                    </tr>
                  ) : (
                    auditLogs.map(record => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="p-3">{formatDateTime(record.timestamp)}</td>
                        <td className="p-3">
                          <span className="capitalize">{record.record_type}</span>
                          <div className="text-xs text-gray-500">{record.record_id?.substring(0, 8)}...</div>
                        </td>
                        <td className="p-3">{getActionBadge(record.action)}</td>
                        <td className="p-3">
                          <div className="font-medium">{record.changed_by_name}</div>
                          <div className="text-xs text-gray-500">{getRoleBadge(record.changed_by_role)}</div>
                        </td>
                        <td className="p-3 text-gray-600">{record.field_changed || 'Full record'}</td>
                        <td className="p-3">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => viewRecordDetail(record, 'audit')}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {auditTotal > pageSize && (
              <div className="p-4 border-t flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Page {page + 1} of {Math.ceil(auditTotal / pageSize)}
                </span>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={(page + 1) * pageSize >= auditTotal}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deleted Records Tab */}
      {activeSubTab === 'deleted-records' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2 text-gray-700 font-medium">
              <Filter className="w-4 h-4" />
              Filters
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {!isHospitalAdmin && (
                <div>
                  <Label className="text-xs">Hospital</Label>
                  <Select 
                    value={deletedFilters.hospital_id || 'all'} 
                    onValueChange={(v) => setDeletedFilters({...deletedFilters, hospital_id: v === 'all' ? '' : v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="All Hospitals" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Hospitals</SelectItem>
                      {hospitals.map(h => (
                        <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs">Record Type</Label>
                <Select 
                  value={deletedFilters.record_type || 'all'} 
                  onValueChange={(v) => setDeletedFilters({...deletedFilters, record_type: v === 'all' ? '' : v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="patient">Patient</SelectItem>
                    <SelectItem value="prescription">Prescription</SelectItem>
                    <SelectItem value="appointment">Appointment</SelectItem>
                    <SelectItem value="doctor">Doctor</SelectItem>
                    <SelectItem value="front_desk_staff">Front Desk Staff</SelectItem>
                    <SelectItem value="medical_assistant">Medical Assistant</SelectItem>
                    <SelectItem value="hospital">Hospital</SelectItem>
                    <SelectItem value="vaccination">Vaccination</SelectItem>
                    <SelectItem value="growth_record">Growth Record</SelectItem>
                    <SelectItem value="medical_condition">Medical Condition</SelectItem>
                    <SelectItem value="allergy">Allergy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">From Date</Label>
                <Input 
                  type="date" 
                  value={deletedFilters.start_date}
                  onChange={(e) => setDeletedFilters({...deletedFilters, start_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">To Date</Label>
                <Input 
                  type="date" 
                  value={deletedFilters.end_date}
                  onChange={(e) => setDeletedFilters({...deletedFilters, end_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input 
                    type="checkbox" 
                    checked={deletedFilters.include_restored}
                    onChange={(e) => setDeletedFilters({...deletedFilters, include_restored: e.target.checked})}
                    className="rounded"
                  />
                  Include Restored
                </label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleApplyFilters} size="sm">
                <Search className="w-4 h-4 mr-1" /> Apply
              </Button>
              <Button variant="outline" onClick={handleResetFilters} size="sm">
                Reset
              </Button>
            </div>
          </div>

          {/* Results */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {deletedTotal} deleted records found
              </span>
              <Button variant="ghost" size="sm" onClick={loadData} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium">Deleted At</th>
                    <th className="text-left p-3 font-medium">Record Type</th>
                    <th className="text-left p-3 font-medium">Deleted By</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {deletedRecords.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-gray-500">
                        No deleted records found
                      </td>
                    </tr>
                  ) : (
                    deletedRecords.map(record => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="p-3">{formatDateTime(record.deletion_timestamp)}</td>
                        <td className="p-3">
                          <span className="capitalize">{record.record_type}</span>
                          <div className="text-xs text-gray-500">{record.record_id?.substring(0, 8)}...</div>
                        </td>
                        <td className="p-3">
                          <div className="font-medium">{record.deleted_by_name}</div>
                          <div className="text-xs text-gray-500">{getRoleBadge(record.deleted_by_role)}</div>
                        </td>
                        <td className="p-3">
                          {record.is_restored ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3" /> Restored
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <Trash2 className="w-3 h-3" /> Deleted
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => viewRecordDetail(record, 'deleted')}
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {!record.is_restored && !isHospitalAdmin && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleRestoreRecord(record.id)}
                                  className="text-green-600 hover:text-green-700"
                                  title="Restore Record"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => openPermanentDeleteDialog(record)}
                                  className="text-red-600 hover:text-red-700"
                                  title="Permanently Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {deletedTotal > pageSize && (
              <div className="p-4 border-t flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Page {page + 1} of {Math.ceil(deletedTotal / pageSize)}
                </span>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={(page + 1) * pageSize >= deletedTotal}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Failed Logins Tab */}
      {activeSubTab === 'failed-logins' && (
        <div className="space-y-4">
          {/* Time Period Selector */}
          <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-4">
            <Label className="text-sm font-medium">Time Period:</Label>
            <Select 
              value={failedLoginHours.toString()} 
              onValueChange={(v) => {
                setFailedLoginHours(parseInt(v));
                setTimeout(loadFailedLogins, 100);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 1 hour</SelectItem>
                <SelectItem value="6">Last 6 hours</SelectItem>
                <SelectItem value="24">Last 24 hours</SelectItem>
                <SelectItem value="48">Last 48 hours</SelectItem>
                <SelectItem value="168">Last 7 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={loadFailedLogins} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {failedLogins && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-700 font-medium">Total Failed Attempts</p>
                      <p className="text-3xl font-bold text-red-900">{failedLogins.total_failed}</p>
                    </div>
                    <AlertTriangle className="w-10 h-10 text-red-600" />
                  </div>
                </div>
                <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-orange-700 font-medium">Unique Users</p>
                      <p className="text-3xl font-bold text-orange-900">{failedLogins.unique_users}</p>
                    </div>
                    <User className="w-10 h-10 text-orange-600" />
                  </div>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-700 font-medium">Hours Monitored</p>
                      <p className="text-3xl font-bold text-blue-900">{failedLogins.hours_checked}</p>
                    </div>
                    <Clock className="w-10 h-10 text-blue-600" />
                  </div>
                </div>
              </div>

              {/* Summary by User */}
              {failedLogins.summary_by_user && failedLogins.summary_by_user.length > 0 && (
                <div className="bg-white rounded-xl border p-4">
                  <h3 className="font-semibold text-gray-900 mb-4">Failed Attempts by User</h3>
                  <div className="space-y-3">
                    {failedLogins.summary_by_user.map((user, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium">{user.user_name || user.user_id}</p>
                            <p className="text-xs text-gray-500">{getRoleBadge(user.role)}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-red-600">{user.attempts}</p>
                          <p className="text-xs text-gray-500">attempts</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed List */}
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="p-4 border-b bg-gray-50">
                  <h3 className="font-semibold text-gray-900">Detailed Failed Login Attempts</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 font-medium">Time</th>
                        <th className="text-left p-3 font-medium">User</th>
                        <th className="text-left p-3 font-medium">Role</th>
                        <th className="text-left p-3 font-medium">IP Address</th>
                        <th className="text-left p-3 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {failedLogins.details.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-gray-500">
                            No failed login attempts in this period
                          </td>
                        </tr>
                      ) : (
                        failedLogins.details.map((record, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="p-3">{formatDateTime(record.login_time)}</td>
                            <td className="p-3">
                              <div className="font-medium">{record.user_name}</div>
                              <div className="text-xs text-gray-500">{record.user_id}</div>
                            </td>
                            <td className="p-3">{getRoleBadge(record.role)}</td>
                            <td className="p-3 font-mono text-xs">{record.ip_address || 'N/A'}</td>
                            <td className="p-3 text-red-600">{record.failure_reason || 'Unknown'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Summary Tab */}
      {activeSubTab === 'summary' && auditSummary && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700 font-medium">Successful Logins</p>
                  <p className="text-3xl font-bold text-green-900">{auditSummary.login_stats?.successful || 0}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </div>
            <div className="bg-red-50 rounded-xl p-4 border border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-700 font-medium">Failed Logins</p>
                  <p className="text-3xl font-bold text-red-900">{auditSummary.login_stats?.failed || 0}</p>
                </div>
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700 font-medium">Total Changes</p>
                  <p className="text-3xl font-bold text-blue-900">
                    {Object.values(auditSummary.actions || {}).reduce((a, b) => a + b, 0)}
                  </p>
                </div>
                <FileText className="w-10 h-10 text-blue-600" />
              </div>
            </div>
            <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-700 font-medium">Deleted Records</p>
                  <p className="text-3xl font-bold text-orange-900">{auditSummary.deleted_records || 0}</p>
                </div>
                <Trash2 className="w-10 h-10 text-orange-600" />
              </div>
            </div>
          </div>

          {/* Actions Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Changes by Action Type</h3>
              <div className="space-y-3">
                {Object.entries(auditSummary.actions || {}).map(([action, count]) => (
                  <div key={action} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getActionBadge(action)}
                    </div>
                    <span className="text-lg font-semibold">{count}</span>
                  </div>
                ))}
                {Object.keys(auditSummary.actions || {}).length === 0 && (
                  <p className="text-gray-500 text-center py-4">No data changes in this period</p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Changes by Record Type</h3>
              <div className="space-y-3">
                {Object.entries(auditSummary.record_types || {}).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="capitalize text-gray-700">{type}</span>
                    <span className="text-lg font-semibold">{count}</span>
                  </div>
                ))}
                {Object.keys(auditSummary.record_types || {}).length === 0 && (
                  <p className="text-gray-500 text-center py-4">No record changes in this period</p>
                )}
              </div>
            </div>
          </div>

          {/* Top Changers */}
          <div className="bg-white rounded-xl border p-4">
            <h3 className="font-semibold text-gray-900 mb-4">Top Users by Activity</h3>
            <div className="space-y-3">
              {(auditSummary.top_changers || []).map((user, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-bold text-blue-700">
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-medium">{user.user_name}</p>
                      <p className="text-xs text-gray-500">{getRoleBadge(user.role)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-blue-600">{user.changes}</p>
                    <p className="text-xs text-gray-500">changes</p>
                  </div>
                </div>
              ))}
              {(auditSummary.top_changers || []).length === 0 && (
                <p className="text-gray-500 text-center py-4">No user activity in this period</p>
              )}
            </div>
          </div>

          <div className="text-center text-sm text-gray-500">
            Data from the last {auditSummary.period_days} days
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {detailType === 'login' && 'Login Details'}
              {detailType === 'audit' && 'Audit Log Details'}
              {detailType === 'deleted' && 'Deleted Record Details'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRecord && (
            <div className="space-y-4">
              {detailType === 'login' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">User ID</Label>
                      <p className="font-medium">{selectedRecord.user_id}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">User Name</Label>
                      <p className="font-medium">{selectedRecord.user_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Role</Label>
                      <p>{getRoleBadge(selectedRecord.role)}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Status</Label>
                      <p>{getStatusBadge(selectedRecord.status)}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Login Time</Label>
                      <p className="font-medium">{formatDateTime(selectedRecord.login_time)}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Logout Time</Label>
                      <p className="font-medium">{formatDateTime(selectedRecord.logout_time) || 'Active'}</p>
                    </div>
                  </div>
                  <div className="border-t pt-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">IP Address:</span>
                      <span className="font-mono text-sm">{selectedRecord.ip_address || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Monitor className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Device:</span>
                      <span className="text-sm truncate max-w-md">{selectedRecord.device_info || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Location:</span>
                      <span className="text-sm">{selectedRecord.location || 'N/A'}</span>
                    </div>
                    {selectedRecord.failure_reason && (
                      <div className="bg-red-50 p-3 rounded-lg">
                        <span className="text-sm text-red-700">
                          <strong>Failure Reason:</strong> {selectedRecord.failure_reason}
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {detailType === 'audit' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Record Type</Label>
                      <p className="font-medium capitalize">{selectedRecord.record_type}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Record ID</Label>
                      <p className="font-mono text-sm">{selectedRecord.record_id}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Action</Label>
                      <p>{getActionBadge(selectedRecord.action)}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Timestamp</Label>
                      <p className="font-medium">{formatDateTime(selectedRecord.timestamp)}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Changed By</Label>
                      <p className="font-medium">{selectedRecord.changed_by_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Role</Label>
                      <p>{getRoleBadge(selectedRecord.changed_by_role)}</p>
                    </div>
                  </div>
                  {selectedRecord.field_changed && (
                    <div className="border-t pt-4">
                      <Label className="text-xs text-gray-500">Field Changed</Label>
                      <p className="font-medium">{selectedRecord.field_changed}</p>
                    </div>
                  )}
                  {(selectedRecord.old_value || selectedRecord.new_value) && (
                    <div className="border-t pt-4 grid grid-cols-2 gap-4">
                      {selectedRecord.old_value && (
                        <div>
                          <Label className="text-xs text-gray-500">Old Value</Label>
                          <pre className="mt-1 p-2 bg-red-50 rounded text-sm overflow-auto max-h-40">
                            {selectedRecord.old_value}
                          </pre>
                        </div>
                      )}
                      {selectedRecord.new_value && (
                        <div>
                          <Label className="text-xs text-gray-500">New Value</Label>
                          <pre className="mt-1 p-2 bg-green-50 rounded text-sm overflow-auto max-h-40">
                            {selectedRecord.new_value}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {detailType === 'deleted' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Record Type</Label>
                      <p className="font-medium capitalize">{selectedRecord.record_type}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Original Record ID</Label>
                      <p className="font-mono text-sm">{selectedRecord.record_id}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Deleted By</Label>
                      <p className="font-medium">{selectedRecord.deleted_by_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Deleted At</Label>
                      <p className="font-medium">{formatDateTime(selectedRecord.deletion_timestamp)}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Status</Label>
                      <p>
                        {selectedRecord.is_restored ? (
                          <span className="text-green-600">Restored</span>
                        ) : (
                          <span className="text-red-600">Deleted</span>
                        )}
                      </p>
                    </div>
                    {selectedRecord.is_restored && (
                      <div>
                        <Label className="text-xs text-gray-500">Restored By</Label>
                        <p className="font-medium">{selectedRecord.restored_by_name}</p>
                      </div>
                    )}
                  </div>
                  <div className="border-t pt-4">
                    <Label className="text-xs text-gray-500 mb-2 block">Original Record Data</Label>
                    <pre className="p-3 bg-gray-50 rounded-lg text-sm overflow-auto max-h-60">
                      {JSON.stringify(selectedRecord.record_data, null, 2)}
                    </pre>
                  </div>
                  {!selectedRecord.is_restored && !isHospitalAdmin && (
                    <div className="border-t pt-4 flex gap-2">
                      <Button 
                        onClick={() => {
                          handleRestoreRecord(selectedRecord.id);
                          setShowDetailDialog(false);
                        }}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Restore This Record
                      </Button>
                      <Button 
                        onClick={() => {
                          setShowDetailDialog(false);
                          openPermanentDeleteDialog(selectedRecord);
                        }}
                        variant="destructive"
                        className="flex-1"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Permanently Delete
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog open={showPermanentDeleteDialog} onOpenChange={setShowPermanentDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Permanent Delete
            </DialogTitle>
          </DialogHeader>
          
          {recordToDelete && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium mb-2">
                  ⚠️ This action is IRREVERSIBLE
                </p>
                <p className="text-sm text-red-700">
                  You are about to permanently delete this <strong className="capitalize">{recordToDelete.record_type}</strong> record.
                  This cannot be undone and the data will be lost forever.
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Record Details</div>
                <div className="text-sm">
                  <div><strong>Type:</strong> <span className="capitalize">{recordToDelete.record_type}</span></div>
                  <div><strong>ID:</strong> {recordToDelete.record_id?.substring(0, 20)}...</div>
                  <div><strong>Deleted:</strong> {formatDateTime(recordToDelete.deletion_timestamp)}</div>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">
                  Type <span className="text-red-600 font-bold">DELETE</span> to confirm
                </Label>
                <Input 
                  value={permanentDeleteConfirmText}
                  onChange={(e) => setPermanentDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE here"
                  className="mt-2"
                  data-testid="permanent-delete-confirm-input"
                />
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowPermanentDeleteDialog(false);
                    setRecordToDelete(null);
                    setPermanentDeleteConfirmText('');
                  }}
                  className="flex-1"
                  disabled={deletingPermanently}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handlePermanentDelete}
                  className="flex-1"
                  disabled={permanentDeleteConfirmText !== 'DELETE' || deletingPermanently}
                  data-testid="confirm-permanent-delete-btn"
                >
                  {deletingPermanently ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Permanently Delete
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecurityAuditTab;
